import { AppLayout } from "@/components/AppLayout";
import { ScheduleCalendar } from "@/components/ScheduleCalendar";
import { useI18n } from "@/i18n/I18nContext";
import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { creditTypeLabel } from "@/lib/creditTypes";

export function BookSchedule() {
  const { t, tl, lang } = useI18n();
  const [reloadKey, setReloadKey] = useState(0);
  const bump = () => setReloadKey((k) => k + 1);

  const book = async (session) => {
    try {
      await api.post(`/sessions/${session.session_id}/book`);
      toast.success(t("booking.booked"));
      bump();
    } catch (err) {
      const code = err.response?.status;
      const detail = err.response?.data?.detail;
      if (code === 402) {
        const need = detail?.required_type;
        const label = need ? creditTypeLabel(need, lang) : "";
        const msg = need
          ? tl({ es: `Esta clase requiere un pack ${label}. Tus créditos actuales no son válidos para ella.`,
                 en: `This class requires a ${label} pack. Your current credits are not valid for it.`,
                 fr: `Ce cours nécessite un pack ${label}. Tes crédits actuels ne sont pas valables pour celui-ci.` })
          : t("booking.noCredit");
        toast.error(msg, { duration: 9000, action: { label: tl({ es: `Ver packs ${label}`, en: `See ${label} packs`, fr: `Voir les packs ${label}` }), onClick: () => window.location.assign(need ? `/app/credits?need=${need}` : "/app/credits") } });
      } else if (code === 425) {
        toast.error(t("booking.notOpen").replace("{days}", detail?.open_days ?? ""));
      } else if (detail === "booking_closed") {
        toast.error(t("booking.closed"));
      } else {
        toast.error(formatApiError(detail));
      }
    }
  };

  const cancel = async (session) => {
    if (!session.my_reservation) return;
    if (session.cancel_late && !window.confirm(tl({
      es: `Es una cancelación fuera de plazo (menos de ${session.cancel_hours_limit}h): perderás tu crédito. ¿Continuar?`,
      en: `This is a late cancellation (under ${session.cancel_hours_limit}h): you will lose your credit. Continue?`,
      fr: `C'est une annulation hors délai (moins de ${session.cancel_hours_limit}h) : tu perdras ton crédit. Continuer ?`,
    }))) return;
    try {
      const { data } = await api.post(`/reservations/${session.my_reservation}/cancel`);
      toast.success(data.refunded
        ? tl({ es: "Reserva cancelada, crédito devuelto", en: "Booking cancelled, credit refunded", fr: "Réservation annulée, crédit remboursé" })
        : tl({ es: "Reserva cancelada (crédito perdido)", en: "Booking cancelled (credit lost)", fr: "Réservation annulée (crédit perdu)" }));
      bump();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const waitlist = async (session) => {
    try {
      const { data } = await api.post(`/sessions/${session.session_id}/waitlist`);
      toast.success(`${t("booking.waitlisted")} (#${data.position})`);
      bump();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <AppLayout>
      <h1 className="mb-6 font-display text-3xl font-bold tracking-tight">{t("schedule.title")}</h1>
      <ScheduleCalendar onBook={book} onWaitlist={waitlist} onCancel={cancel} reloadKey={reloadKey} />
    </AppLayout>
  );
}
